<template>
  <Panel
    :header="filterHeaderString"
    :toggleable="true"
    :collapsed="true"
    class="mt-3"
  >
    <form
      @submit.prevent="validateFilters"
    >
      <div class="p-input mb-3">
        <label
          for="stakeholderId"
        >{{ $t('entity.lineId.stakeholderId') }}</label>
        <InputText
          id="stakeholderId"
          v-model.trim="v$.filterStakeholderId.$model"
          class="col-12 mt-2"
          type="text"
          :class="{'invalid-input': v$.filterStakeholderId.$error }"
          autocomplete="on"
        />
      </div>
      <div class="grid">
        <div class="col">
          <div class="p-input mb-3">
            <label
              for="duplexHigh"
            >{{ $t('entity.lineId.duplexHigh') }}</label>
            <InputText
              id="duplexHigh"
              v-model.trim="v$.filterDuplexHigh.$model"
              class="col-12 mt-2"
              type="text"
              :class="{'invalid-input': v$.filterDuplexHigh.$error }"
              autocomplete="on"
            />
          </div>
        </div>
        <div class="col">
          <div class="p-input mb-3">
            <label
              for="duplexLow"
            >{{ $t('entity.lineId.duplexLow') }}</label>
            <InputText
              id="duplexLow"
              v-model.trim="v$.filterDuplexLow.$model"
              class="col-12 mt-2"
              type="text"
              :class="{'invalid-input': v$.filterDuplexLow.$error }"
              autocomplete="on"
            />
          </div>
        </div>
      </div>
      <div class="grid">
        <div class="col-12 md:col-6">
          <div class="p-input mb-3">
            <label
              for="licencePeriods"
            >{{ $t('entity.lineId.listLicencePeriods') }}</label>
            <MultiSelect
              id="licencePeriods"
              v-model="v$.filterLicencePeriod.$model"
              class="col-12 p-0 mt-2"
              :class="{'invalid-input': v$.filterLicencePeriod.$error }"
              :options="periods"
              :option-label="(value) => $t(`entity.lineId.licencePeriods.${value}`)"
            />
            <div
              v-for="error of v$.filterLicencePeriod.$errors"
              :key="error.$uid"
              class="input-errors"
            >
              <div class="error-msg">
                {{ error.$message }}
              </div>
            </div>
          </div>
        </div>
        <div class="col-12 md:col-6">
          <div class="p-input mb-3">
            <label
              for="mainVenues"
            >{{ $t('entity.lineId.listMainVenues') }}</label>
            <MultiSelect
              id="mainVenues"
              v-model="v$.filterMainVenue.$model"
              class="col-12 p-0 mt-2"
              :class="{'invalid-input': v$.filterMainVenue.$error }"
              :options="venues"
              option-value="id"
              :option-label="(value) => $i18n.locale === 'fr' ? value.frenchWording : value.englishWording"
            />
            <div
              v-for="error of v$.filterMainVenue.$errors"
              :key="error.$uid"
              class="input-errors"
            >
              <div class="error-msg">
                {{ error.$message }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="grid">
        <div class="col-12 md:col-6">
          <div class="p-input mb-3">
            <label
              for="startDate"
            >{{ $t('entity.lineId.startDate') }}</label>
            <Calendar
              id="startDate"
              v-model="start"
              class="col-12 p-0 mt-2"
              date-format="dd-mm-yy"
              @date-select="changeFormat"
              @input="changeFormat"
            />
            <div
              v-for="error of v$.filterStartDate.$errors"
              :key="error.$uid"
              class="input-errors"
            >
              <div class="error-msg">
                {{ error.$message }}
              </div>
            </div>
          </div>
        </div>
        <div class="col-12 md:col-6">
          <div class="p-input mb-3">
            <label
              for="endDate"
            >{{ $t('entity.lineId.endDate') }}</label>
            <Calendar
              id="endDate"
              v-model="end"
              class="col-12 p-0 mt-2"
              date-format="dd-mm-yy"
              @date-select="changeFormat"
              @input="changeFormat"
            />
          </div>
          <div
            v-for="error of v$.filterEndDate.$errors"
            :key="error.$uid"
            class="input-errors"
          >
            <div class="error-msg">
              {{ error.$message }}
            </div>
          </div>
        </div>
      </div>
      <div class="p-input mb-3 col-6 pl-0 pt-0">
        <label
          for="code"
        >{{ $t('entity.barcode.code') }}</label>
        <InputNumber
          id="code"
          v-model="v$.filterCode.$model"
          class="col-12 p-0 mt-2"
          :class="{'invalid-input': v$.filterCode.$error }"
          autocomplete="on"
        />
      </div>

      <Message
        v-if="errors"
        severity="error"
        :closable="false"
      >
        <ul class="list-none m-0 p-0">
          <li
            v-for="error of errors"
            :key="error.content"
          >
            {{ $t(error) }}
          </li>
        </ul>
      </Message>
      <div class="flex justify-content-end">
        <Button
          :label="$t('action.reset')"
          icon="pi pi-times"
          class="p-button-rounded p-button-outlined p-button-secondary mr-2"
          @click="resetFilter"
        />
        <Button
          :label="$t('action.search')"
          type="submit"
          icon="pi pi-check"
          class="p-button-rounded p-button-outlined"
        />
      </div>
    </form>
  </Panel>
</template>

<script>
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Calendar from "primevue/calendar";
import Panel from "primevue/panel";
import Message from "primevue/message";
import Button from "primevue/button";
import {useVuelidate} from "@vuelidate/core";
import MultiSelect from "primevue/multiselect";
import moment from "moment";
import {reactive} from "vue";
import useVenueFilter from "@/composables/filters/useVenueFilter";

export default {
  name: "BarcodeAdvancedFilter",
  components: {InputText, InputNumber, Calendar, Panel, Message, Button, MultiSelect},
  props: {
    stakeholderId: {
      type: String,
      default: null
    },
    mainVenue: {
      type: Array,
      default: () => []
    },
    licencePeriod: {
      type: Array,
      default: () => []
    },
    duplexHigh: {
      type: String,
      default: null
    },
    duplexLow: {
      type: String,
      default: null
    },
    startDate: {
      type: String,
      default: null
    },
    endDate: {
      type: String,
      default: null
    },
    code: {
      type: Number,
      default: null
    }
  },
  emits: ['update:stakeholderId', 'update:mainVenue', 'update:licencePeriod', 'update:duplexHigh', 'update:duplexLow', 'update:startDate', 'update:endDate', 'update:code', 'validate', 'reset'],
  setup() {
    const venueFilter = reactive(useVenueFilter());
    return {
      v$: useVuelidate(),
      venueFilter: venueFilter,
    }
  },
  data() {
    return {
      uniqueSiteCode: this.$store.getters['setting/getUniqueSiteCode'],
      venues: [],
      periods: [],
      start: null,
      end: null,
      isSaving: false,
      errors: null
    }
  },
  computed: {
    uniqueSiteMode() {
      return this.uniqueSiteCode && this.uniqueSiteCode !== '';
    },
    filterStakeholderId: {
      get() {
        return this.stakeholderId
      },
      set(value) {
        this.$emit('update:stakeholderId', value)
      }
    },
    filterMainVenue: {
      get() {
        return this.mainVenue;
      },
      set(value) {
        this.$emit('update:mainVenue', value)
      }
    },
    filterLicencePeriod: {
      get() {
        return this.licencePeriod
      },
      set(value) {
        this.$emit('update:licencePeriod', value)
      }
    },
    filterDuplexHigh: {
      get() {
        return this.duplexHigh
      },
      set(value) {
        this.$emit('update:duplexHigh', value)
      }
    },
    filterDuplexLow: {
      get() {
        return this.duplexLow
      },
      set(value) {
        this.$emit('update:duplexLow', value)
      },
    },
    filterStartDate: {
      get() {
        return this.startDate
      },
      set(value) {
        this.$emit('update:startDate', value)
      }
    },
    filterEndDate: {
      get() {
        return this.endDate
      },
      set(value) {
        this.$emit('update:endDate', value)
      }
    },
    filterCode: {
      get() {
        return this.code
      },
      set(value) {
        this.$emit('update:code', value)
      }
    },
    filterHeaderString() {
      const amountVenuesSelected = this.filterMainVenue?.filter(item => item !== "").length;
      const venuesSelected = this.venues.filter(venue => this.filterMainVenue.includes(venue.id)).map(venue => this.$i18n.locale === 'fr' ? venue.frenchWording : venue.englishWording).toString().replaceAll(',', ', ');
      const filterStr = (amountVenuesSelected && amountVenuesSelected > 0) ? `( ${venuesSelected} )` : '';
      return this.$t('lineidpage.advancedFilters') + ' ' + filterStr;
    }
  },
  validations() {
    return {
      filterStakeholderId: {},
      filterMainVenue: {},
      filterLicencePeriod: {},
      filterDuplexHigh: {},
      filterDuplexLow: {},
      filterStartDate: {},
      filterEndDate: {},
      filterCode: {}
    }
  },
  watch: {
    async filterLicencePeriod() {
      if (this.filterLicencePeriod.length !== 0) {
        this.venues = await this.$lineIdService.getMainVenuesByLicencePeriods(this.filterLicencePeriod);
      }else{
        this.getVenues();
      }

      this.filterMainVenue = [];
    }
  },
  mounted() {
    this.getVenues();
    this.periods = this.$store.getters['setting/getLineIdLicencePeriods'].slice().sort((a,b) => {
      const nameA = this.$t(`entity.lineId.licencePeriods.${a}`).toLowerCase();
      const nameB = this.$t(`entity.lineId.licencePeriods.${b}`).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  },
  methods: {
    async getVenues() {
      this.venues = [];
      try {
        this.venues = (await this.$venueService.fetchVenuesPagingAndSorting(this.venueFilter.getQueryParams())).content;
      } catch (e) {
        console.error(e);
        this.$emitter.emit('toast', { severity: 'error', message: 'error.global' });
      } finally {
        this.isLoading = false;
      }
    },
    async validateFilters() {
      this.errors = null;
      const isFormValid = await this.v$.$validate();
      if (!isFormValid) return;
      let venueCopy;
      if (this.venues.length === this.filterMainVenue.length) {
        venueCopy = [...this.filterMainVenue];
        this.filterMainVenue = [];
      }
      let periodCopy;
      if (this.periods.length === this.filterLicencePeriod.length) {
        periodCopy = [...this.filterLicencePeriod];
        this.filterLicencePeriod = [];
      }

      this.$emit('validate');

      if (venueCopy) this.filterMainVenue = venueCopy;
      if (periodCopy) this.filterLicencePeriod = periodCopy;
    },
    resetFilter() {
      this.start = null;
      this.end = null;
      this.$emit('reset');
    },
    changeFormat() {
      if (this.start) {
        this.v$.filterStartDate.$model = moment(this.start).format('YYYY-MM-DD');
      }
      if (this.end) {
        this.v$.filterEndDate.$model = moment(this.end).format('YYYY-MM-DD');
      }
    }
  }
}
</script>

<style scoped>

</style>
